import { motion } from "framer-motion";
import { useEffect, useMemo, useRef } from "react";

/**
 * Optimizations applied vs. the original, same visual result:
 *
 * 1. Merged 3 separate requestAnimationFrame loops into 1.
 *    Fewer per-frame function calls + fewer separate style writes.
 *
 * 2. Randomized stars / shooting stars / light beams are now generated
 *    ONCE with useMemo instead of on every render. Previously
 *    `Math.random()` ran inline in JSX, so any parent re-render would
 *    regenerate all ~82 elements with new random values.
 *
 * 3. Added `willChange: transform` to every element that moves every
 *    frame (cursor layers, orbiting dots). This promotes them to their
 *    own compositor layer up front, so the browser doesn't have to
 *    keep re-deciding whether to promote them - it just moves the
 *    existing layer instead of repainting blurred/blended content.
 *
 * 4. Animation loop pauses automatically when the tab is hidden
 *    (Page Visibility API), instead of burning CPU/GPU in the
 *    background indefinitely.
 *
 * 5. mousemove listener marked passive (was already non-blocking,
 *    now explicit) and reads straight into a ref, no state/re-render.
 */

const STAR_COUNT = 65;
const SHOOTING_STAR_COUNT = 5;
const BEAM_COUNT = 12;

const STAR_COLORS = ["neon-cyan", "neon-purple", "neon-yellow", "neon-white"];
const SHOOTING_COLORS = ["neon-cyan", "neon-yellow", "neon-white"];
const BEAM_COLORS = ["neon-cyan", "neon-pink", "neon-purple"];

const AnimatedBackground = () => {
  const pinkDotRef = useRef(null);
  const cyanDotRef = useRef(null);

  const cursorAuraRef = useRef(null);
  const cursorGlowRef = useRef(null);
  const cursorCoreRef = useRef(null);

  const mousePos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const smoothPos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  // --- Generate randomized decorative elements ONCE, not every render ---
  const stars = useMemo(
    () =>
      Array.from({ length: STAR_COUNT }, (_, i) => ({
        key: `star-${i}`,
        size: 2 + Math.random() * 3,
        color: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)],
        top: Math.random() * 100,
        left: Math.random() * 100,
        opacity: 0.3 + Math.random() * 0.7,
        duration: 2 + Math.random() * 3,
        delay: Math.random() * 5,
      })),
    []
  );

  const shootingStars = useMemo(
    () =>
      Array.from({ length: SHOOTING_STAR_COUNT }, (_, i) => ({
        key: `shooting-${i}`,
        color: SHOOTING_COLORS[Math.floor(Math.random() * SHOOTING_COLORS.length)],
        top: 5 + Math.random() * 80,
        left: 5 + Math.random() * 80,
        duration: 3 + Math.random() * 4,
        delay: Math.random() * 10,
      })),
    []
  );

  const beams = useMemo(
    () =>
      Array.from({ length: BEAM_COUNT }, (_, i) => ({
        key: `beam-${i}`,
        length: 50 + Math.random() * 150,
        angle: Math.random() * 360,
        color: BEAM_COLORS[Math.floor(Math.random() * BEAM_COLORS.length)],
        top: 10 + Math.random() * 80,
        left: 10 + Math.random() * 80,
        duration: 10 + Math.random() * 20,
      })),
    []
  );

  // --- Track raw mouse position (no re-render) ---
  useEffect(() => {
    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // --- Single merged animation loop for cursor + orbiting dots ---
  useEffect(() => {
    let animId;
    let running = true;

    const centerX = () => window.innerWidth / 2;
    const centerY = () => window.innerHeight / 2;
    const orbitRadius = 200;
    const orbitSpeed = 0.0005;

    const tick = (timestamp) => {
      if (!running) return;

      // Cursor aura/glow easing (lerp follow)
      smoothPos.current.x += (mousePos.current.x - smoothPos.current.x) * 0.15;
      smoothPos.current.y += (mousePos.current.y - smoothPos.current.y) * 0.15;
      const sx = smoothPos.current.x;
      const sy = smoothPos.current.y;
      const scaleWave = 1 + 0.1 * Math.sin(timestamp / 500);

      if (cursorAuraRef.current) {
        cursorAuraRef.current.style.transform = `translate(${sx}px, ${sy}px) translate(-50%, -50%) scale(${scaleWave})`;
      }
      if (cursorGlowRef.current) {
        cursorGlowRef.current.style.transform = `translate(${sx}px, ${sy}px) translate(-50%, -50%)`;
      }
      if (cursorCoreRef.current) {
        cursorCoreRef.current.style.transform = `translate(${mousePos.current.x}px, ${mousePos.current.y}px) translate(-50%, -50%)`;
      }

      // Orbiting pink/cyan "planet + moon" dots
      if (pinkDotRef.current && cyanDotRef.current) {
        const pinkAngle = timestamp * orbitSpeed;
        const pinkX = centerX() + Math.cos(pinkAngle) * orbitRadius;
        const pinkY = centerY() + Math.sin(pinkAngle) * orbitRadius;

        const cyanAngle = timestamp * orbitSpeed * 2;
        const cyanX = pinkX + Math.cos(cyanAngle) * orbitRadius;
        const cyanY = pinkY + Math.sin(cyanAngle) * orbitRadius;

        pinkDotRef.current.style.transform = `translate(${pinkX}px, ${pinkY}px)`;
        cyanDotRef.current.style.transform = `translate(${cyanX}px, ${cyanY}px)`;
      }

      animId = requestAnimationFrame(tick);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(animId);
      } else if (!running) {
        running = true;
        animId = requestAnimationFrame(tick);
      }
    };

    animId = requestAnimationFrame(tick);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(animId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return (
    <>
      {/* ── Background Layer (-z-50) ── */}
      <div className="fixed top-0 left-0 w-full h-full bg-dark -z-50 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5 bg-[size:40px_40px] [mask-image:linear-gradient(to_bottom,transparent,black_20%,black_80%,transparent)]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          }}
        ></div>

        <div className="absolute inset-0 overflow-hidden opacity-02">
          <motion.div
            ref={pinkDotRef}
            className="absolute w-40 h-40 bg-neon-pink rounded-full mix-blend-screen filter blur-2xl"
            style={{ willChange: "transform" }}
            animate={{
              opacity: [0.5, 0.9, 0.5],
              scale: [6.5, 7.5, 6.5],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />

          <motion.div
            ref={cyanDotRef}
            className="absolute w-24 h-24 bg-neon-cyan rounded-full mix-blend-screen filter blur-md"
            style={{ willChange: "transform" }}
            animate={{
              opacity: [0.5, 0.9, 0.5],
              scale: [0.7, 1.1, 0.7],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          />
        </div>

        <div className="absolute inset-0 opacity-50">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-neon-white rounded-full filter blur-2xl animate-float-delay"></div>
          <div
            className="absolute -bottom-32 -right-32 w-80 h-80 rounded-full filter blur-[60px] opacity-70 mix-blend-screen animate-float-delay"
            style={{
              backgroundColor: "#fffb00",
              boxShadow: "0 0 120px 60px rgba(255, 230, 0, 0.3), 0 0 200px 100px rgba(255, 140, 0, 0.2)",
            }}
          ></div>
        </div>

        <motion.div
          className="fixed -left-32 bottom-40 w-80 h-80 bg-neon-green rounded-full mix-blend-screen filter blur-3xl opacity-20 z-0"
          style={{ willChange: "transform, opacity" }}
          animate={{
            opacity: [0.25, 0.4, 0.25],
            scale: [0.95, 1.0, 0.95],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="absolute ttop-1/4 left-1/4 w-[800px] h-[800px] pointer-events-none">
          <div
            className="absolute inset-0 bg-neon-purple/50 rounded-full animate-pulse-slow"
            style={{
              boxShadow: "0 0 200px 150px rgba(176, 0, 240, 0.1)",
              filter: "blur(40px)",
            }}
          ></div>
        </div>

        <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] pointer-events-none">
          <div
            className="absolute inset-0 bg-neon-cyan/10 rounded-full animate-pulse-slow-delay"
            style={{
              boxShadow: "0 0 200px 150px rgba(0, 247, 255, 0.1)",
              filter: "blur(60px)",
            }}
          ></div>
        </div>

        {/* Cyberpunk stars with neon glow — generated once via useMemo */}
        {stars.map((s) => (
          <div
            key={s.key}
            className={`absolute rounded-full bg-${s.color} text-${s.color} drop-shadow-neon-sm animate-twinkle`}
            style={{
              width: `${s.size}px`,
              height: `${s.size}px`,
              borderRadius: "50%",
              top: `${s.top}%`,
              left: `${s.left}%`,
              opacity: s.opacity,
              animationDuration: `${s.duration}s`,
              animationDelay: `${s.delay}s`,
              zIndex: -1,
            }}
          ></div>
        ))}

        {/* Shooting stars — generated once via useMemo */}
        {shootingStars.map((s) => (
          <div
            key={s.key}
            className="absolute"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              transform: "rotate(45deg)",
            }}
          >
            <div
              className={`w-24 h-[2px] rounded-full blur-3xl bg-gradient-to-l from-${s.color} to-transparent drop-shadow-neon animate-shooting-star opacity-0`}
              style={{
                animationDuration: `${s.duration}s`,
                animationDelay: `${s.delay}s`,
              }}
            ></div>
          </div>
        ))}

        {/* Abstract neon light beams — generated once via useMemo */}
        {beams.map((b) => (
          <div
            key={b.key}
            className={`absolute bg-${b.color}/10 animate-pulse-slow`}
            style={{
              width: `${b.length}px`,
              height: "1px",
              top: `${b.top}%`,
              left: `${b.left}%`,
              transform: `rotate(${b.angle}deg)`,
              transformOrigin: "left center",
              boxShadow: `0 0 15px 2px var(--tw-${b.color})`,
              filter: "blur(1px)",
              animationDuration: `${b.duration}s`,
            }}
          ></div>
        ))}
      </div>

      {/* ── Foreground Cursor Layer (z-[9999]) ── */}
      <div className="fixed inset-0 pointer-events-none z-[9999]">
        <div
          ref={cursorAuraRef}
          className="pointer-events-none absolute top-0 left-0"
          style={{
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(0,247,255,0.15) 0%, rgba(0,247,255,0.05) 50%, transparent 70%)",
            filter: "blur(50px)",
            mixBlendMode: "screen",
            willChange: "transform",
          }}
        />

        <div
          ref={cursorGlowRef}
          className="pointer-events-none absolute top-0 left-0"
          style={{
            width: "45px",
            height: "45px",
            borderRadius: "50%",
            border: "2px solid rgba(0,247,255,0.8)",
            background: "radial-gradient(circle, rgba(0,247,255,0.15) 0%, transparent 70%)",
            boxShadow: "0 0 15px 0 rgba(0,247,255,0.4), inset 0 0 10px 0 rgba(0,247,255,0.4)",
            filter: "blur(0.5px)",
            mixBlendMode: "screen",
            willChange: "transform",
          }}
        />

        <div
          ref={cursorCoreRef}
          className="pointer-events-none absolute top-0 left-0"
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 0 10px 3px rgba(0,247,255,0.9), 0 0 20px 6px rgba(0,247,255,0.5)",
            mixBlendMode: "screen",
            willChange: "transform",
          }}
        />
      </div>

      <style>{`
        * { cursor: none !important; }
      `}</style>
    </>
  );
};

export default AnimatedBackground;